import { memo, useState, useMemo, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { has, isEmpty, isNil, last } from 'lodash';
// Import Services
import { ABTestingHttpService, CampaignHttpService, PromotionHttpService } from 'services/http';
import { Helpers } from 'services';
// Import Components
import ConfirmationModal from 'components/common/Modals/ConfirmationModal';
import ButtonWithLoader from 'components/common/ButtonWithLoader';
import { CampaignInfoModal, CampaignAddGoalModal } from 'components/CampaignModal';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useToaster from 'hooks/UseToaster';
import useHasPermission from 'hooks/UseHasPermission';
// Import Config
import { modalsStateSetDictKeys } from 'pages/CampaignListPage/config';
// Import UI Components
import { Alert, Button, Icon, Modal } from '@geneui/components';
// Import Enums
import { ABTestStatusEnum, SubscriptionSectionEnum } from 'enums';
// Import Constants
import {
    ModalsActions,
    l,
    ActionsIcons,
    ModalsClassNames,
    FilterComparisons,
    GoalOptions,
    AlertTypes,
    PromotionTypesLabels,
} from 'constants/common';
// Import Styles
import 'assets/scss/campaignModal.scss';
import styles from './campaignActionsModals.module.scss';

const { success } = AlertTypes;
const { isCurrentDateWithinPeriod } = Helpers;

interface Props {
    isDeleteModalOpenedState: boolean;
    isArchiveModalOpenedState: boolean;
    isUnArchiveModalOpenedState: boolean;
    isStopSendModalOpenedState: boolean;
    isInfoModalOpenedState: boolean;
    isAddGoalModalOpenedState: boolean;
    isDeleteGoalModalOpenedState: boolean;
    isMakePublicModalOpenedState?: boolean;
    isMakePrivateModalOpenedState?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    openedModalData: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    templateProviders?: Record<string, any>;
    isStopABTestModalOpenedState: boolean;
    isInReportPage?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleInfoModalActionClick?: (key?: string, campaign?: Record<string, any>) => void;
    onCloseModalHandler: (
        key: string,
        action?: string,
        alertType?: string,
        alertMessage?: Nullable<string>,
        count?: number,
    ) => void;
}

const CampaignActionsModals: FC<Props> = (props) => {
    const {
        isDeleteGoalModalOpenedState,
        isDeleteModalOpenedState,
        isArchiveModalOpenedState,
        isUnArchiveModalOpenedState,
        isStopSendModalOpenedState,
        isInfoModalOpenedState,
        isAddGoalModalOpenedState,
        isStopABTestModalOpenedState,
        isMakePublicModalOpenedState,
        isMakePrivateModalOpenedState,
        openedModalData,
        templateProviders,
        isInReportPage,
        handleInfoModalActionClick,
        onCloseModalHandler,
    } = props;
    const { t } = useTranslation();

    const { showToaster } = useToaster();
    const { doDeleteRequest, doPostRequest } = useRequest();
    const { hasSubscriptionSection } = useHasPermission();

    const [isLoading, setIsLoading] = useState(false);

    const {
        archiveCampaignRequest,
        archiveCampaignsRequest,
        unarchiveCampaignRequest,
        unarchiveCampaignsRequest,
        deleteCampaignRequest,
        deleteCampaignsRequest,
        stopSendCampaignRequest,
        createGoalCampaign,
        deleteGoalCampaignRequest,
        stopCampaignABTestRequest,
        setPromotionIsPublicRequest,
    } = useCancelableRequests({
        deleteCampaignRequest: CampaignHttpService.deleteCampaign,
        deleteCampaignsRequest: CampaignHttpService.deleteCampaigns,
        archiveCampaignRequest: CampaignHttpService.archiveCampaign,
        archiveCampaignsRequest: CampaignHttpService.archiveCampaigns,
        unarchiveCampaignRequest: CampaignHttpService.unarchiveCampaign,
        unarchiveCampaignsRequest: CampaignHttpService.unarchiveCampaigns,
        stopSendCampaignRequest: CampaignHttpService.stopSendCampaigns,
        createGoalCampaign: CampaignHttpService.setCampaignGoal,
        deleteGoalCampaignRequest: CampaignHttpService.deleteCampaignGoal,
        stopCampaignABTestRequest: ABTestingHttpService.stopCampaignABTest,
        setPromotionIsPublicRequest: PromotionHttpService.setPromotionIsPublic,
    });

    const deleteCampaign = () => {
        setIsLoading(true);
        doDeleteRequest(deleteCampaignRequest.request, {
            queryString: { campaignId: openedModalData.CampaignId },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            if (IsCanceled) return;
            onCloseModalHandler(modalsStateSetDictKeys.deleteCampaign, ModalsActions.DELETE, AlertType, AlertMessage);
            setIsLoading(false);
        });
    };

    const deleteCampaigns = () => {
        setIsLoading(true);
        const { allowDeleteCampaigns } = openedModalData;
        if (isEmpty(allowDeleteCampaigns)) {
            return onCloseModalHandler(modalsStateSetDictKeys.deleteCampaign);
        }
        const allowDeleteCampaignsIds = allowDeleteCampaigns.map(({ campaignId }) => {
            return campaignId;
        });
        doDeleteRequest(deleteCampaignsRequest.request, {
            queryString: allowDeleteCampaignsIds,
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDictKeys.deleteCampaign,
                ModalsActions.DELETE,
                AlertType,
                AlertMessage,
                allowDeleteCampaignsIds.length,
            );
            setIsLoading(false);
        });
    };

    const archiveCampaign = () => {
        setIsLoading(true);
        doPostRequest(archiveCampaignRequest.request, {
            requestBody: null,
            queryString: { campaignId: openedModalData.CampaignId },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            if (IsCanceled) return;
            onCloseModalHandler(modalsStateSetDictKeys.archiveCampaign, ModalsActions.ARCHIVE, AlertType, AlertMessage);
            setIsLoading(false);
        });
    };

    const archiveCampaigns = () => {
        setIsLoading(true);
        const { allowArchiveCampaigns } = openedModalData;
        if (isEmpty(allowArchiveCampaigns)) {
            return onCloseModalHandler(modalsStateSetDictKeys.archiveCampaign);
        }
        const allowArchiveCampaignsIds = allowArchiveCampaigns.map(({ campaignId }) => {
            return campaignId;
        });
        doPostRequest(archiveCampaignsRequest.request, {
            requestBody: allowArchiveCampaignsIds,
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDictKeys.archiveCampaign,
                ModalsActions.ARCHIVE,
                AlertType,
                AlertMessage,
                allowArchiveCampaignsIds.length,
            );
            setIsLoading(false);
        });
    };

    const unarchiveCampaign = () => {
        setIsLoading(true);
        doPostRequest(unarchiveCampaignRequest.request, {
            requestBody: null,
            queryString: { campaignId: openedModalData.CampaignId },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDictKeys.unarchiveCampaign,
                ModalsActions.UNARCHIVE,
                AlertType,
                AlertMessage,
            );
            setIsLoading(false);
        });
    };

    const unarchiveCampaigns = () => {
        setIsLoading(true);
        const { allowUnarchiveCampaigns } = openedModalData;
        if (isEmpty(allowUnarchiveCampaigns)) {
            return onCloseModalHandler(modalsStateSetDictKeys.unarchiveCampaign);
        }
        const allowUnarchiveCampaignsIds = allowUnarchiveCampaigns.map(({ campaignId }) => {
            return campaignId;
        });
        doPostRequest(unarchiveCampaignsRequest.request, {
            requestBody: allowUnarchiveCampaignsIds,
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDictKeys.unarchiveCampaign,
                ModalsActions.UNARCHIVE,
                AlertType,
                AlertMessage,
                allowUnarchiveCampaignsIds.length,
            );
            setIsLoading(false);
        });
    };

    const stopSendCampaign = () => {
        setIsLoading(true);
        doPostRequest(stopSendCampaignRequest.request, {
            requestBody: null,
            queryString: { campaignId: openedModalData.CampaignId },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            if (IsCanceled) return;
            onCloseModalHandler(modalsStateSetDictKeys.stopSendCampaign, ModalsActions.STOP, AlertType, AlertMessage);
            setIsLoading(false);
        });
    };

    const setGoalCampaign = (type, count) => {
        setIsLoading(true);
        doPostRequest(createGoalCampaign.request, {
            requestBody: {
                campaignId: openedModalData?.CampaignId,
                Comparision: FilterComparisons.GreaterOrEquals,
                Name: `${GoalOptions[type]} = ${count}`,
                Type: type,
                Value: count,
            },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDictKeys.setGoalCampaign,
                ModalsActions.SET_GOAL,
                AlertType,
                AlertMessage,
            );
            setIsLoading(false);
        });
    };
    const deleteGoalCampaign = () => {
        setIsLoading(true);
        doDeleteRequest(deleteGoalCampaignRequest.request, {
            queryString: {
                id: openedModalData?.GoalId,
            },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDictKeys.deleteGoalCampaign,
                ModalsActions.DELETE_GOAL,
                AlertType,
                AlertMessage,
            );
            setIsLoading(false);
        });
    };

    const stopABTest = () => {
        setIsLoading(true);
        doPostRequest(stopCampaignABTestRequest.request, {
            queryString: { campaignId: openedModalData.CampaignId },
            successCallback: () => {
                if (shouldStopWithFailedStatus) {
                    showToaster(success, t(l.TheABTestWasStoppedWithFailedStatus));
                } else {
                    showToaster(success, t(l.TheABTestWasStopped));
                }
            },
        }).then(({ IsCanceled }) => {
            if (IsCanceled) return;
            onCloseModalHandler(modalsStateSetDictKeys.stopABTest, ModalsActions.STOP_AB_TEST);
            setIsLoading(false);
        });
    };

    const makePublicCampaigns = () => {
        setIsLoading(true);
        const { allowMakePublicCampaigns } = openedModalData;
        if (isEmpty(allowMakePublicCampaigns)) {
            return onCloseModalHandler(modalsStateSetDictKeys.makePublicCampaign);
        }
        const allowMakePublicCampaignsIds = allowMakePublicCampaigns.map(({ campaignId }) => {
            return campaignId;
        });
        doPostRequest(setPromotionIsPublicRequest.request, {
            queryString: {
                isPublic: true,
            },
            requestBody: {
                [PromotionTypesLabels.Campaign]: allowMakePublicCampaignsIds,
            },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDictKeys.makePublicCampaign,
                ModalsActions.MAKE_PUBLIC,
                AlertType,
                AlertMessage,
                allowMakePublicCampaignsIds.length,
            );
            setIsLoading(false);
        });
    };

    const makePrivateCampaigns = () => {
        setIsLoading(true);
        const { allowMakePrivateCampaigns } = openedModalData;
        if (isEmpty(allowMakePrivateCampaigns)) {
            return onCloseModalHandler(modalsStateSetDictKeys.makePrivateCampaign);
        }
        const allowMakePrivateCampaignsIds = allowMakePrivateCampaigns.map(({ campaignId }) => {
            return campaignId;
        });
        doPostRequest(setPromotionIsPublicRequest.request, {
            queryString: {
                isPublic: false,
            },
            requestBody: {
                [PromotionTypesLabels.Campaign]: allowMakePrivateCampaignsIds,
            },
            isShowError: false,
        }).then(({ AlertMessage, AlertType, IsCanceled }) => {
            if (IsCanceled) return;
            onCloseModalHandler(
                modalsStateSetDictKeys.makePrivateCampaign,
                ModalsActions.MAKE_PRIVATE,
                AlertType,
                AlertMessage,
                allowMakePrivateCampaignsIds.length,
            );
            setIsLoading(false);
        });
    };

    const shouldStopWithFailedStatus = useMemo(() => {
        if (isNil(openedModalData)) {
            return false;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const lastABTest: Nilable<Record<string, any>> = last(openedModalData.ABTests || []);

        if (!lastABTest) {
            return false;
        }
        const abTestStartDate = lastABTest.StartDate || openedModalData.NextRunDate;

        return (
            lastABTest.Status === ABTestStatusEnum.InProgress &&
            !isNil(abTestStartDate) &&
            isCurrentDateWithinPeriod(abTestStartDate, lastABTest.AnalyticsPeriod || 0)
        );
    }, [openedModalData]);

    const hasUserRestrictionOnExistingPlan = useMemo(
        () => !hasSubscriptionSection(SubscriptionSectionEnum.AIPoweredABTesting),
        [hasSubscriptionSection],
    );

    const isBulkAction = useMemo(() => {
        return (
            has(openedModalData, 'allowDeleteCampaigns') ||
            has(openedModalData, 'allowArchiveCampaigns') ||
            has(openedModalData, 'allowUnarchiveCampaigns')
        );
    }, [openedModalData]);

    return isEmpty(openedModalData) ? null : (
        <>
            {isStopABTestModalOpenedState && (
                <Modal
                    withPortal={true}
                    background="dark-background"
                    position="top"
                    title={t(l.StopABTest)}
                    size="content-size"
                    className="crm-modal"
                    appearance="compact"
                    closable={false}
                    closeOnClickOutside={true}
                    visible={isStopABTestModalOpenedState}
                    footer={
                        <>
                            <Button
                                flexibility="default"
                                color="default"
                                appearance="minimal"
                                onClick={() => onCloseModalHandler(modalsStateSetDictKeys.stopABTest)}
                            >
                                {t(l.Cancel)}
                            </Button>
                            <ButtonWithLoader
                                flexibility="default"
                                color="danger"
                                onClick={stopABTest}
                                isLoading={isLoading}
                                isDisabled={isLoading}
                            >
                                {t(l.Stop)}
                            </ButtonWithLoader>
                        </>
                    }
                >
                    <div className={styles.stopABTestModalContent}>
                        <div className="confirmation-modal-warning-icon-container">
                            <div className="crm-circle-icon-cnt">
                                <Icon type="bc-icon-test-stop" />
                            </div>
                            <div className="crm-allowd-not-allowd-cnt">
                                {t(l.AreYouSureYouWantToStopTheABTest, {
                                    abTestIndex: `${openedModalData?.ABTests?.length}`,
                                })}
                            </div>
                        </div>
                        {hasUserRestrictionOnExistingPlan && (
                            <Alert
                                closable="false"
                                title={t(l.WarningMessage)}
                                message={t(l.ABTestCompletedManagementUnavailableOnExistingPlan)}
                                type="warning"
                                iconProps={{ type: 'bc-icon-info' }}
                            />
                        )}
                        {shouldStopWithFailedStatus && (
                            <Alert
                                closable="false"
                                title={t(l.WarningMessage)}
                                message={t(l.TheABTestWillBeFailedAnalyticPeriodNotPassed)}
                                type="warning"
                                iconProps={{ type: 'bc-icon-info' }}
                            />
                        )}
                    </div>
                </Modal>
            )}

            {isDeleteModalOpenedState && (
                <ConfirmationModal
                    onOk={isBulkAction ? deleteCampaigns : deleteCampaign}
                    isOkActive={isBulkAction ? !isEmpty(openedModalData.allowDeleteCampaigns) : true}
                    onCancel={() => onCloseModalHandler(modalsStateSetDictKeys.deleteCampaign)}
                    isVisibleModal={isDeleteModalOpenedState}
                    titleText={isBulkAction ? t(l.DeleteSelectedCampaigns) : t(l.DeleteCampaign)}
                    isLoading={isLoading}
                    iconType={ActionsIcons.Delete}
                    actionLabel={t(l.Delete)}
                    className={ModalsClassNames.Delete}
                    allowDataList={openedModalData.allowDeleteCampaigns}
                    notAllowDataList={openedModalData.notAllowDeleteCampaigns}
                    allowDataLabel={l.AllowDeleteSelectedCampaigns}
                    notAllowDataLabel={l.NotAllowDeleteSelectedCampaigns}
                    questionLabel={
                        isBulkAction
                            ? l.DeleteSelectedCampaignsQuestion
                            : t(l.AreYouSureYouWantToDeleteCampaign, {
                                  campaignName: `"${openedModalData.Name.Name}"`,
                              })
                    }
                    isBulkAction={isBulkAction}
                />
            )}

            {isArchiveModalOpenedState && (
                <ConfirmationModal
                    onOk={isBulkAction ? archiveCampaigns : archiveCampaign}
                    isOkActive={isBulkAction ? !isEmpty(openedModalData.allowArchiveCampaigns) : true}
                    onCancel={() => onCloseModalHandler(modalsStateSetDictKeys.archiveCampaign)}
                    isVisibleModal={isArchiveModalOpenedState}
                    titleText={isBulkAction ? t(l.ArchiveSelectedCampaigns) : t(l.ArchiveCampaign)}
                    isLoading={isLoading}
                    iconType={ActionsIcons.Archive}
                    actionLabel={t(l.Archive)}
                    className={ModalsClassNames.Archive}
                    allowDataList={openedModalData.allowArchiveCampaigns}
                    notAllowDataList={openedModalData.notAllowArchiveCampaigns}
                    allowDataLabel={l.AllowArchiveSelectedCampaigns}
                    notAllowDataLabel={l.NotAllowArchiveSelectedCampaigns}
                    questionLabel={
                        isBulkAction
                            ? l.ArchiveSelectedCampaignsQuestion
                            : t(l.AreYouSureYouWantToArchiveCampign, {
                                  campaignName: `"${openedModalData.Name.Name}"`,
                              })
                    }
                    isBulkAction={isBulkAction}
                />
            )}

            {isUnArchiveModalOpenedState && (
                <ConfirmationModal
                    onOk={isBulkAction ? unarchiveCampaigns : unarchiveCampaign}
                    isOkActive={isBulkAction ? !isEmpty(openedModalData.allowUnarchiveCampaigns) : true}
                    onCancel={() => onCloseModalHandler(modalsStateSetDictKeys.unarchiveCampaign)}
                    isVisibleModal={isUnArchiveModalOpenedState}
                    titleText={isBulkAction ? t(l.UnarchiveSelectedCampaigns) : t(l.UnarchiveCampaign)}
                    isLoading={isLoading}
                    iconType={ActionsIcons.UnArchive}
                    actionLabel={t(l.Unarchive)}
                    className={ModalsClassNames.UnArchive}
                    allowDataList={openedModalData.allowUnarchiveCampaigns}
                    notAllowDataList={openedModalData.notAllowUnarchiveCampaigns}
                    allowDataLabel={l.AllowUnarchiveSelectedCampaigns}
                    notAllowDataLabel={l.NotAllowUnarchiveSelectedCampaigns}
                    questionLabel={
                        isBulkAction
                            ? l.UnarchiveSelectedCampaignsQuestion
                            : t(l.AreYouSureYouWantToUnarchiveCampaign, {
                                  campaignName: `"${openedModalData.Name.Name}"`,
                              })
                    }
                    isBulkAction={isBulkAction}
                />
            )}

            {isStopSendModalOpenedState && (
                <ConfirmationModal
                    onOk={stopSendCampaign}
                    onCancel={() => onCloseModalHandler(modalsStateSetDictKeys.stopSendCampaign)}
                    isVisibleModal={isStopSendModalOpenedState}
                    titleText={t(l.StopCampaign)}
                    questionLabel={t(l.AreYouSureYouWantToStopCampign, {
                        campaignName: `"${openedModalData.Name.Name}"`,
                    })}
                    isLoading={isLoading}
                    iconType={ActionsIcons.Stop}
                    actionLabel={t(l.Stop)}
                    className={ModalsClassNames.Stop}
                />
            )}

            {isInfoModalOpenedState && (
                <CampaignInfoModal
                    campaignId={openedModalData.CampaignId}
                    handleClose={() => onCloseModalHandler(modalsStateSetDictKeys.infoCampaign)}
                    isVisible={isInfoModalOpenedState}
                    handleInfoModalActionClick={handleInfoModalActionClick}
                    templateProviders={templateProviders}
                    isInReportPage={isInReportPage}
                />
            )}

            {isAddGoalModalOpenedState && (
                <CampaignAddGoalModal
                    data={openedModalData}
                    isVisibleModal={isAddGoalModalOpenedState}
                    onCancel={() => onCloseModalHandler(modalsStateSetDictKeys.setGoalCampaign)}
                    onOk={setGoalCampaign}
                    isLoading={isLoading}
                />
            )}

            {isDeleteGoalModalOpenedState && (
                <ConfirmationModal
                    onOk={deleteGoalCampaign}
                    onCancel={() => onCloseModalHandler(modalsStateSetDictKeys.deleteGoalCampaign)}
                    isVisibleModal={isDeleteGoalModalOpenedState}
                    titleText={t(l.DeleteGoalCampaign)}
                    questionLabel={t(l.AreYouSureYouWantToDeleteGoalCampaign, {
                        campaignName: `"${openedModalData.Name.Name}"`,
                    })}
                    isLoading={isLoading}
                    iconType={ActionsIcons.Delete}
                    actionLabel={t(l.Delete)}
                    className={ModalsClassNames.Delete}
                />
            )}

            {isMakePublicModalOpenedState && (
                <ConfirmationModal
                    isVisibleModal={true}
                    isBulkAction={true}
                    onOk={makePublicCampaigns}
                    isOkActive={!isEmpty(openedModalData.allowMakePublicCampaigns)}
                    onCancel={() => onCloseModalHandler(modalsStateSetDictKeys.makePublicCampaign)}
                    titleText={t(l.MakePublicSelectedCampaigns)}
                    isLoading={isLoading}
                    actionLabel={t(l.MakePublic)}
                    allowDataList={openedModalData.allowMakePublicCampaigns}
                    notAllowDataList={openedModalData.notAllowMakePublicCampaigns}
                    allowDataLabel={l.AllowMakePublicSelectedCampaigns}
                    notAllowDataLabel={l.NotAllowMakePublicSelectedCampaigns}
                    questionLabel={l.MakePublicSelectedCampaignsQuestion}
                />
            )}

            {isMakePrivateModalOpenedState && (
                <ConfirmationModal
                    isVisibleModal={true}
                    isBulkAction={true}
                    onOk={makePrivateCampaigns}
                    isOkActive={!isEmpty(openedModalData.allowMakePrivateCampaigns)}
                    onCancel={() => onCloseModalHandler(modalsStateSetDictKeys.makePrivateCampaign)}
                    titleText={t(l.MakePrivateSelectedCampaigns)}
                    isLoading={isLoading}
                    actionLabel={t(l.MakePrivate)}
                    allowDataList={openedModalData.allowMakePrivateCampaigns}
                    notAllowDataList={openedModalData.notAllowMakePrivateCampaigns}
                    allowDataLabel={l.AllowMakePrivateSelectedCampaigns}
                    notAllowDataLabel={l.NotAllowMakePrivateSelectedCampaigns}
                    questionLabel={l.MakePrivateSelectedCampaignsQuestion}
                />
            )}
        </>
    );
};

export default memo(CampaignActionsModals);
